/**
 * Journey entity adapter (P16.3 / #485).
 *
 * Journey rows are (IBS, channel, description) tuples. The drawer
 * hosts a nested JourneyStepsSection so steps are edited inline
 * under their parent journey.
 *
 * Quirks vs other adapters:
 *   - No `code` column -- the natural key is (ibs, channel)
 *   - List columns show resolved IBS + channel names via truncated
 *     UUID for now; resolved-name display lands with #531
 *   - Required entityPicker for both ibs_service_id + channel_id
 */
import { i18n } from "shared/i18n";
import { JourneyStepsSection } from "./JourneyStepsSection";
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface Journey {
  id: string;
  ibs_service_id: string;
  channel_id: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  // P272.4: resolved by the list endpoint's join (register shows names).
  ibs_service_name?: string | null;
  channel_name?: string | null;
}

export interface JourneyIn {
  ibs_service_id: string;
  channel_id: string;
  description: string | null;
}

export type JourneyPatch = Partial<JourneyIn>;

const columns: AdminColumn<Journey>[] = [
  {
    key: "ibs_service_id",
    label: "IBS",
    sortValue: (r) => r.ibs_service_name ?? r.ibs_service_id,
    render: (r) => (
      <span
        data-testid="journey-ibs-cell"
        className="text-xs text-slate-700"
        title={r.ibs_service_id}
      >
        {r.ibs_service_name ?? `${r.ibs_service_id.slice(0, 8)}...`}
      </span>
    ),
  },
  {
    key: "channel_id",
    label: "Channel",
    sortValue: (r) => r.channel_name ?? r.channel_id,
    render: (r) => (
      <span
        data-testid="journey-channel-cell"
        className="text-xs text-slate-700"
        title={r.channel_id}
      >
        {r.channel_name ?? `${r.channel_id.slice(0, 8)}...`}
      </span>
    ),
  },
  {
    key: "description",
    label: "Description",
    sortValue: (r) => r.description ?? "",
    render: (r) =>
      r.description ? (
        <span className="text-xs text-slate-600 line-clamp-1">{r.description}</span>
      ) : (
        <span className="text-xs italic text-slate-400">
          {i18n.t("screens.journeyAdapter.noNarrative")}
        </span>
      ),
  },
  {
    key: "created_at",
    label: "Created",
    sortValue: (r) => r.created_at,
    render: (r) => (
      <span className="text-xs text-slate-500 tabular-nums">
        {r.created_at.slice(0, 10)}
      </span>
    ),
  },
];

const formFields: FormField<JourneyIn>[] = [
  {
    kind: "entityPicker",
    key: "ibs_service_id",
    label: "IBS",
    entityKind: "ibs",
    required: true,
  },
  {
    kind: "entityPicker",
    key: "channel_id",
    label: "Channel",
    entityKind: "channel",
    required: true,
  },
  { kind: "textarea", key: "description", label: "Description", rows: 4 },
];

export const journeyAdapter: EntityAdapter<Journey, JourneyIn, JourneyPatch> = {
  i18nPrefix: "modules.foundation.admin.journeys",
  importKind: "journey",
  endpoint: "/v1/admin/foundation/journeys",
  queryKey: ["foundation-admin", "journeys"] as const,
  columns,
  defaultSort: { key: "created_at", dir: "desc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.ibs_service_id} ${r.channel_id} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    ibs_service_id: "",
    channel_id: "",
    description: null,
  }),
  rowToPatch: (r) => ({
    ibs_service_id: r.ibs_service_id,
    channel_id: r.channel_id,
    description: r.description,
  }),
  extraSections: [JourneyStepsSection],
};
